import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Plus } from 'lucide-react';
import { Link } from 'react-router-dom';

import { formatDate } from '@/shared/lib/dates';

interface DayContextMenuProps {
  day: string;
  position: { x: number; y: number };
  onClose: () => void;
}

/** Respiro entre o menu e a borda da janela, quando ele precisa encostar. */
const EDGE_GAP = 8;

/**
 * Botão direito sobre um dia → **"Novo projeto neste dia"** (spec-calendar.md
 * §4.3, decisão 75).
 *
 * Uma opção só, e ela só é montada para quem tem `writeDraft` — quem decide isso
 * é a página, com a mesma fonte que autoriza a escrita de verdade. Um menu com
 * item desabilitado ensinaria o voluntário que o painel tem portas fechadas;
 * um menu que não abre não ensina nada, que é o certo aqui.
 *
 * **É o único ato de escrita da tela**, e ele não escreve nada: leva ao
 * formulário com a data preenchida. Nada na grade pode ser arrastado, editado ou
 * apagado (ver `ProjectCalendar`).
 *
 * O menu **cresce do ponto do cursor** (`origin-top-left` + `menu-in`), e não do
 * centro da tela: quando algo aparece a partir de onde você clicou, a relação
 * entre o gesto e o resultado não precisa ser explicada.
 *
 * ── ⚠️ POR QUE ISTO É PORTALADO (spec-calendar-ux.md §8.2, decisão 86) ────────
 *
 * **Sintoma:** o menu abria deslocado, para baixo e para a direita do cursor.
 *
 * **Causa, e não era o cálculo das coordenadas.** `position: fixed` é relativo à
 * janela — *exceto* quando um ancestral tem `transform` diferente de `none`:
 * nesse caso o ancestral vira o **bloco contido** dos descendentes `fixed` (CSS
 * Transforms Level 1). O ancestral aqui é o `<main>` do `AppShell`, que carrega
 * `animate-page-in`, declarada com preenchimento **`both`** — e com `both` o
 * último quadro PERSISTE depois de a animação acabar. O último quadro é
 * `transform: translateY(0)`, que não é `none`. O `<main>` fica sendo bloco
 * contido para sempre, e o desvio é exatamente o canto dele: barra lateral +
 * centragem na horizontal, cabeçalho + recheio na vertical.
 *
 * **A correção é o portal, não tirar a animação.** `document.body` não tem
 * `transform`, então `fixed` volta a ser relativo à janela. É pelo mesmo motivo
 * que a `Sheet` nunca teve o problema: ela já renderiza em
 * `createPortal(…, document.body)`.
 *
 * O encosto na borda passa a ser MEDIDO (`getBoundingClientRect`) em vez de
 * adivinhado por duas constantes escritas à mão — que, além de frágeis,
 * ignoravam a altura real do menu.
 */
export function DayContextMenu({ day, position, onClose }: DayContextMenuProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [placement, setPlacement] = useState(position);

  /**
   * `useLayoutEffect` e não `useEffect`: a correção precisa estar aplicada
   * ANTES da pintura. Com `useEffect`, o menu apareceria por um quadro vazando
   * pela borda e pularia depois — trocaria um defeito de posição por um de
   * movimento.
   */
  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;

    const rect = node.getBoundingClientRect();
    setPlacement({
      x: Math.max(EDGE_GAP, Math.min(position.x, window.innerWidth - rect.width - EDGE_GAP)),
      y: Math.max(EDGE_GAP, Math.min(position.y, window.innerHeight - rect.height - EDGE_GAP)),
    });
  }, [position]);

  useEffect(() => {
    // Foco no menu recém-aberto: quem navega por teclado precisa chegar nele, e
    // `Esc` precisa de um alvo. Sem isto o menu seria só para mouse.
    ref.current?.querySelector('a')?.focus();

    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    function onPointer(event: MouseEvent) {
      if (!ref.current?.contains(event.target as Node)) onClose();
    }

    document.addEventListener('keydown', onKey);
    // `capture`: fechar antes que o clique chegue à grade e selecione outro dia.
    document.addEventListener('mousedown', onPointer, true);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer, true);
    };
  }, [onClose]);

  return createPortal(
    <div
      ref={ref}
      role="menu"
      aria-label={`Ações para ${formatDate(`${day}T12:00:00-03:00`)}`}
      style={{ position: 'fixed', left: placement.x, top: placement.y }}
      className="z-50 w-[15rem] origin-top-left animate-menu-in rounded-lg border border-border bg-popover p-1 shadow-lg"
    >
      <Link
        role="menuitem"
        to={`/calendario/novo?data=${day}`}
        onClick={onClose}
        className="flex items-center gap-2 rounded-md px-2.5 py-2 text-sm font-medium text-popover-foreground transition-colors duration-fast ease-out hover:bg-accent focus-visible:bg-accent focus-visible:outline-none"
      >
        <Plus aria-hidden="true" className="size-4 text-muted-foreground" />
        Novo projeto neste dia
      </Link>
      <p className="px-2.5 pb-1 pt-0.5 text-xs text-muted-foreground">
        {formatDate(`${day}T12:00:00-03:00`)}
      </p>
    </div>,
    document.body,
  );
}
